import { createSelector, createStructuredSelector } from 'reselect';
import {
  AddressFieldsForSubmit,
  ChildAddressForSubmit,
  MarriageForSubmit,
  SpouseAddressForSubmit,
  SubmitModelProps,
} from '../models/submit';
import {
  ADDRESS_FIELDS_TO_SEND,
  APPLICANT_FIELDS_TO_SEND,
  PERSON_FIELDS_TO_SEND,
} from '../constants/fieldNamesToSend';
import { isEmptyObject } from '../helpers/differenceObject';
import { ChildFields } from '../models/child';
import { ApplicantFields } from '../models/applicant';
import { Elk } from '../models/elk';
import { getNotHiddenChildren } from './children';
import {
  addressToSubmit,
  addStreetForZelenograd,
  applicantToSubmit,
  checkChildIsAdult,
  childAddressToSubmit,
  childForSubmit,
  documentForSubmit,
  personToSubmit,
  secondParentToSubmit,
} from '../helpers/toSendHelpers';
import { formatDateToSubmit, isAdult } from '../helpers/dateHelpers';
import { getValue } from '../constants/getFieldValues';
import { CITIZENSHIP } from '../constants/citizenship';
import {
  getActiveChildBirthCertificate,
  getActiveChildPassport,
} from './child';
import { getSpouse, spouseAddressComplete } from './spouse';
import { ADDRESS_MATCH } from '../constants/spouse';
import {
  getApplicant,
  getApplicantAddress,
  getApplicantDocument,
  getApplicantIsAddressCompleted,
  getApplicantLivingTogetherToSubmit,
  getApplicantMarriedOrLivingTogether,
  getApplicantSexValue,
  isApplicantMarried,
} from './applicant';
import { AgreementsProps } from '../models/agreements';
import { LoadingState } from '../declarations/widget-ums';
import { getLoadingState } from './addressUnmodeled';
import { DOCUMENT_TYPE } from '../constants/document';

export const getAgreements: (state: {
  agreements: AgreementsProps;
}) => AgreementsProps = (state) => state.agreements;

export const agreementsComponentSelector = createSelector(
  getAgreements,
  getApplicantSexValue,
  ({ warnedAboutResponsibility, consentWithTerms }, applicantSex) => ({
    warnedAboutResponsibility,
    consentWithTerms,
    applicantSex,
  })
);

export const getNeedPaperCertificateChecked = createSelector(
  getAgreements,
  ({ needPaperCertificate }) => !!needPaperCertificate.checked
);

export const getApplicantAddressForSubmit: (state: {
  applicant: ApplicantFields;
}) => AddressFieldsForSubmit = createSelector(
  getApplicantAddress,
  (address) => {
    return {
      ...addressToSubmit(address),
    };
  }
);

const getApplicantPersonalInfoForSubmit: ((state: {
  applicant: ApplicantFields;
}) => Record<string, unknown>) & {
  resultFunc: (res: ApplicantFields) => Record<string, unknown>;
  recomputations: () => number;
  resetRecomputations: () => number;
} = createSelector(getApplicant, (applicant) => applicantToSubmit(applicant));

const getApplicantDocumentForSubmit: (state: {
  applicant: ApplicantFields;
}) => { citizenship: any } = createSelector(
  getApplicant,
  getApplicantDocument,
  ({ citizenship, birthPlace }, document) => {
    return {
      citizenship: CITIZENSHIP[getValue(citizenship)],
      ...documentForSubmit(document, birthPlace),
    };
  }
);

const getApplicantMarriageForSubmit: ((state: {
  applicant: ApplicantFields;
}) => {
  marriage: MarriageForSubmit;
} | null) & {
  resultFunc: (
    res1: boolean,
    res2: ApplicantFields
  ) => {
    marriage: MarriageForSubmit;
  } | null;
  recomputations: () => number;
  resetRecomputations: () => number;
} = createSelector(
  isApplicantMarried,
  getApplicant,
  (
    isMarried,
    { dateOfMarriage, marriageRecordNumber, marriageRecordHolder }
  ) => {
    return isMarried
      ? {
          marriage: {
            date_of_marriage: formatDateToSubmit(getValue(dateOfMarriage)),
            act_number: getValue(marriageRecordNumber),
            act_issued_by: getValue(marriageRecordHolder),
          },
        }
      : null;
  }
);

const getApplicantSecondaryFieldsForSubmit = createStructuredSelector({
  address: getApplicantAddressForSubmit,
  document: getApplicantDocumentForSubmit,
  [APPLICANT_FIELDS_TO_SEND.married]: isApplicantMarried,
  [APPLICANT_FIELDS_TO_SEND.livingTogether]: getApplicantLivingTogetherToSubmit,
});

const applicantForSubmit = createSelector(
  getApplicantMarriageForSubmit,
  getApplicantPersonalInfoForSubmit,
  getApplicantSecondaryFieldsForSubmit,
  (marriage, ...applicantData) => ({
    applicant: Object.assign({}, ...applicantData, { ...marriage }),
  })
);

export const spouseAddressForSubmit: (state: {
  applicant: ApplicantFields;
}) => SpouseAddressForSubmit = createSelector(
  getSpouse,
  getApplicantAddressForSubmit,
  (
    { address, addressMatchWithApplicant, isRegisteredInMoscow },
    applicantAddress
  ) => {
    const matchWithApplicantValue = getValue(addressMatchWithApplicant);
    if (!isRegisteredInMoscow) {
      return {
        [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]:
          matchWithApplicantValue === ADDRESS_MATCH.notMatch
            ? ADDRESS_MATCH.notMatch
            : null,
      };
    }

    if (matchWithApplicantValue === ADDRESS_MATCH.match) {
      return {
        ...applicantAddress,
        [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]: ADDRESS_MATCH.match,
      };
    }
    return {
      ...addressToSubmit(address),
      [ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant]:
        matchWithApplicantValue === ADDRESS_MATCH.notMatch
          ? ADDRESS_MATCH.notMatch
          : null,
    };
  }
);

const childrenForSubmit: (state: {
  children: ChildFields;
  applicant: ApplicantFields;
  elk: Elk;
}) => Array<{
  address: ChildAddressForSubmit;
  birthCertificate: { [p: string]: string; citizenship: string };
  document?: { [p: string]: string; citizenship: string };
  id: string;
  is_born_in_current_marriage?: boolean | null;
}> = createSelector(
  getNotHiddenChildren,
  getApplicantAddressForSubmit,
  spouseAddressForSubmit,
  ({ elk }: { elk: Elk }) => elk,
  (children, applicantAddress, spouseAddress, elk) => {
    const childrenData = children.map((child: ChildFields) => {
      const {
        birthdate,
        bornInNotCurrentMarriage,
        secondParent,
        citizenship,
        address,
        addressMatchWithApplicant,
        birthPlace,
        isRegisteredInMoscow,
        id,
        ...rest
      } = child;

      const childData = childForSubmit({
        ...rest,
        birthdate,
        bornInNotCurrentMarriage,
        isRegisteredInMoscow,
      });
      const childAddress = isAdult(getValue(birthdate))
        ? null
        : childAddressToSubmit(
            addressMatchWithApplicant,
            address,
            applicantAddress,
            spouseAddress,
            isRegisteredInMoscow
          );

      const secondParentData = secondParentToSubmit(secondParent);

      const citizenshipValue = getValue<CITIZENSHIP>(citizenship);
      const isForeignCitizen = citizenshipValue !== CITIZENSHIP.CITIZEN;
      const birthCertificate: Record<string, unknown> = documentForSubmit(
        getActiveChildBirthCertificate({
          child,
          elk,
        }),
        birthPlace
      );
      const identificationDocument = documentForSubmit(
        getActiveChildPassport({ child, elk }),
        birthPlace
      );

      const isAdultChild = checkChildIsAdult(birthdate, identificationDocument);

      const citizenshipValueForSubmit = CITIZENSHIP[citizenshipValue];
      return {
        ...childData,
        birthCertificate: {
          citizenship: citizenshipValueForSubmit,
          ...birthCertificate,
        },
        address: childAddress,
        ...secondParentData,
        ...(isAdultChild || isForeignCitizen
          ? {
              document: {
                citizenship: citizenshipValueForSubmit,
                ...identificationDocument,
              },
            }
          : {}),
        id,
      };
    });

    return childrenData;
  }
);

const removeOnlyDraftFields = (person) => {
  if (!person) return person;

  const onlyForDraftFields = [
    ADDRESS_FIELDS_TO_SEND['addressMatchWithApplicant'],
    PERSON_FIELDS_TO_SEND['isRegisteredInMoscow'],
  ];
  onlyForDraftFields.forEach((key) => {
    if (person.hasOwnProperty(key)) {
      delete person[key];
    }
    if (person['address']?.hasOwnProperty(key)) {
      delete person['address'][key];
      if (isEmptyObject(person['address'])) {
        person['address'] = null;
      }
    }
  });
  return person;
};

export const spouseForDraft: (state: {
  applicant: ApplicantFields;
}) => null | {
  address: SpouseAddressForSubmit;
  document: { citizenship: any } | null;
} = createSelector(
  getApplicantMarriedOrLivingTogether,
  getSpouse,
  spouseAddressForSubmit,
  (isMarriedOrLivingTogether, spouse, spouseAddress) => {
    if (!isMarriedOrLivingTogether) return null;

    const citizenshipValue = CITIZENSHIP[getValue(spouse.citizenship)];

    const document = citizenshipValue
      ? {
          citizenship: citizenshipValue,
          ...documentForSubmit(spouse.dul, spouse.birthPlace),
        }
      : null;

    return {
      ...personToSubmit(spouse),
      document,
      address: { ...spouseAddress },
    };
  }
);

const spouseForSubmit = createSelector(spouseForDraft, (spouse) => {
  const spouseWithZelenogradStreet = addStreetForZelenograd(spouse);
  return { spouse: removeOnlyDraftFields(spouseWithZelenogradStreet) };
});

export const submitSelector = createSelector(
  applicantForSubmit,
  spouseForSubmit,
  childrenForSubmit,
  getNeedPaperCertificateChecked,
  (applicant, spouse, childrenForSubmit, needPaperCertificateValue) => {
    const applicantData = addStreetForZelenograd(applicant.applicant);
    const spouseData = addStreetForZelenograd(spouse.spouse);
    const childrenRemovedForDraft = childrenForSubmit.map(
      removeOnlyDraftFields
    );
    const children = childrenRemovedForDraft.map(addStreetForZelenograd);

    return {
      applicant: applicantData,
      spouse: spouseData,
      children,
      get_certificate_on_paper: needPaperCertificateValue,
    };
  }
);

export const submitDraftSelector = createSelector(
  applicantForSubmit,
  spouseForDraft,
  childrenForSubmit,
  getNeedPaperCertificateChecked,
  (applicant, spouse, children, needPaperCertificateValue) => {
    return {
      ...applicant,
      spouse,
      children,
      get_certificate_on_paper: needPaperCertificateValue,
    };
  }
);

export const getFormState: (state: {
  form: SubmitModelProps;
}) => SubmitModelProps = (state) => state.form;

export const getLastSaveToDraftTime: (state: {
  form: SubmitModelProps;
}) => string = createSelector(getFormState, ({ savedTime }) => savedTime);

export const getDraftTime: (state: {
  form: SubmitModelProps;
}) => string = createSelector(getFormState, ({ draftTime }) => draftTime);

export const getRecoveredFromDraft: (state: {
  form: SubmitModelProps;
}) => boolean = createSelector(
  getFormState,
  ({ recoveredFromDraft }) => recoveredFromDraft
);

export const draftDownloadError: (state: {
  form: SubmitModelProps;
}) => boolean = createSelector(
  getFormState,
  ({ draftDownloadError }) => draftDownloadError
);

export const paperCertificateSelector = createSelector(
  getAgreements,
  ({ needPaperCertificate, centerPGU }) => ({ needPaperCertificate, centerPGU })
);

export const isSubmitting: (state: {
  loading: LoadingState;
}) => boolean = createSelector(getLoadingState, ({ effects = {} }) =>
  Boolean(effects['form/submit'])
);

export const isStatsSending: (state: {
  form: SubmitModelProps;
}) => boolean = createSelector(getFormState, ({ readyNamespaces }) =>
  Object.keys(readyNamespaces).every((key) => Boolean(readyNamespaces[key]))
);

export const isAllChildrenBornInCurrentMarriage = createSelector(
  childrenForSubmit,
  (children): string => {
    const allBornInCurrentMarriage = children.every(
      ({ is_born_in_current_marriage }) => Boolean(is_born_in_current_marriage)
    );
    return allBornInCurrentMarriage
      ? 'born_in_current_marriage'
      : 'not_born_in_current_marriage';
  }
);

export const isFinalStepCompleted = createSelector(
  getAgreements,
  getApplicantIsAddressCompleted,
  spouseAddressComplete,
  isSubmitting,
  (
    { warnedAboutResponsibility, consentWithTerms },
    isAddressCompleted,
    spouseAddressComplete,
    isSubmitting
  ) => ({
    complete: warnedAboutResponsibility.checked && consentWithTerms.checked,
    disabled: !(isAddressCompleted && spouseAddressComplete),
    isSubmitting,
  })
);

const getBirthCeritficateInputType = (
  currentChildId: string,
  elkChildren,
  BC: { [p: string]: string }
): string => {
  const childELK = elkChildren.find(
    (child) => child.item_id === currentChildId
  );
  const from_elk =
    childELK &&
    childELK.birth_certificate_number === BC.number &&
    DOCUMENT_TYPE[BC.type] === DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF;
  return from_elk ? 'LK' : 'manual_input';
};

export const getChildrenBirthCertificates = createSelector(
  childrenForSubmit,
  ({ elk }: { elk: Elk }) => elk,
  (children, elk) => {
    return children.map(({ birthCertificate, id }, index) => {
      const input_type = getBirthCeritficateInputType(
        id,
        elk.children,
        birthCertificate
      );
      const birthCertificateInput = birthCertificate.input_type
        ? birthCertificate.input_type
        : input_type;
      return {
        [index]: {
          birthCertificate: {
            type: birthCertificate.type,
            issued_at: birthCertificate.issued_at,
            issued_by: birthCertificate.issued_by,
            number: birthCertificate.number,
            number_input_type: birthCertificateInput,
            place_of_birth: birthCertificate.place_of_birth,
          },
        },
      };
    });
  }
);
